import React, { useState, useEffect } from "react";
import ClayModal, { useModal } from "@clayui/modal";
import ClayButton from "@clayui/button";
import ClayForm, { ClayInput, ClaySelect } from "@clayui/form";
import ClayLayout from "@clayui/layout";
import DatePicker from "react-datepicker"; // Ensure you have this installed
import "react-datepicker/dist/react-datepicker.css"; // Import CSS for DatePicker

function EnergyDataModal({ observer, onClose, onSave, selectedData, mode, selectedTemplate }) {
    const [year, setYear] = useState(null);
    const [dataFields, setDataFields] = useState([]);
    const [error, setError] = useState(null); // State to manage error messages
    const hiddenDataKeys = ["ROW-ORDER"];

    const isTextField = (field) => {
        return ["REGION", "LOCATION", "COAL-TYPE"].includes(field.key);
    };

    const getFieldDisplayValue = (rawValue) => {
        if (rawValue && typeof rawValue === "object" && "value" in rawValue) {
            return rawValue.value ?? "";
        }
        return rawValue ?? "";
    };

    useEffect(() => {
        if (mode === "edit") {
            if (!selectedTemplate) {
                setError("Template not found. Please select a valid template.");
                return; // Exit early if the template is not found
            }

            if (selectedData) {
                setYear(new Date(selectedData.year, 0, 1)); // Set year to a date object (January 1st of the year)
                const existingDataFields = selectedTemplate.fields.map((field) => {
                const rawValue = selectedData.data[field.key];

                    return {
                        key: field.key,
                        fieldName: field.fieldName,
                        fieldUnit: field.fieldUnit,
                        value: getFieldDisplayValue(rawValue),
                        selectValues: field.selectValues || [],
                        isTextField: isTextField(field),
                    };
                });

                setDataFields(existingDataFields);
                setError(null); // Clear error if template is valid
            }
        } else if (mode === "create") {
            if (selectedTemplate) {
                setYear(null); // Reset year
                const newDataFields = selectedTemplate.fields.map((field) => ({
                    key: field.key,
                    fieldName: field.fieldName,
                    fieldUnit: field.fieldUnit,
                    value: "", // Default to empty for new entries
                    selectValues: field.selectValues || [],
                    isTextField: isTextField(field),
                }));
                setDataFields(newDataFields);
                setError(null); // Clear error in create mode
            }
        }
    }, [mode, selectedData, selectedTemplate]);

    const handleFieldChange = (index, value) => {
        const updatedFields = [...dataFields];

        // If the field has selectable values, store both `code` and `description`
        if (updatedFields[index].selectValues.length > 0) {
            const selectedOption = updatedFields[index].selectValues.find(option => option.code === value);
            updatedFields[index].value = selectedOption ? { code: selectedOption.code, description: selectedOption.description } : null;
        } else if (updatedFields[index].isTextField) {
            updatedFields[index].value = value;
        } else {
            // Ensure numbers are stored as numbers, but retain input during typing
            //Debug, allow '-' 
            updatedFields[index].value = /^-?\d*\.?\d*$/.test(value) ? value : updatedFields[index].value;
        }

        setDataFields(updatedFields);
    };

    const handleYearChange = (date) => {
        if (date) {
            setYear(date);
            setError(null); // Clear error when year is selected
        }
    };

    const handleSave = () => {
        if (!year) {
            setError("Year is required.");
            return;
        }

        // Validate mandatory dropdown fields
        for (const field of dataFields) {
            if (field.selectValues.length > 0) {
                if (!field.value || !field.value.code) {
                    setError(`Field "${field.fieldName}" is required.`);
                    return;
                }
            }
        }

        const preservedHiddenData = hiddenDataKeys.reduce((acc, key) => {
            if (selectedData?.data && Object.prototype.hasOwnProperty.call(selectedData.data, key)) {
                acc[key] = selectedData.data[key];
            }
            return acc;
        }, {});

        const formData = {
            id: selectedData?.id || null,
            year: year.getFullYear(),
            type: selectedTemplate.type,
            category: selectedTemplate.category,
            unit: selectedTemplate.unit,
            createdDate: selectedData?.createdDate || new Date().toISOString(),
            data: dataFields.reduce((acc, field) => {
                let fieldValue = field.value;

                if (typeof fieldValue === "string" && !isNaN(fieldValue) && fieldValue.trim() !== "") {
                    fieldValue = parseFloat(fieldValue);
                }

                if (field.selectValues.length > 0) {
                    const selectedOption = field.selectValues.find(option => option.code === fieldValue?.code);
                    acc[field.key] = selectedOption
                        ? { code: selectedOption.code, description: selectedOption.description }
                        : null;
                } else if (field.isTextField) {
                    acc[field.key] = fieldValue;
                } else {
                    acc[field.key] = {
                        value: fieldValue,
                        unit: field.fieldUnit || selectedTemplate.unit || ""
                    };
                }

                return acc;
            }, preservedHiddenData),
        };

        console.log("Form Data being saved: ", formData);
        onSave(formData);
    };


    return (
        <ClayModal observer={observer} size="lg">
            <ClayModal.Header>{mode === "edit" ? "Edit Energy Data" : "Add Energy Data"}</ClayModal.Header>
            <ClayModal.Body>
                {error && <div className="alert alert-danger">{error}</div>} {/* Display error message */}
                <ClayForm>
                    <ClayLayout.Row>
                        <ClayLayout.Col size={12}>
                            <ClayForm.Group>
                                <label htmlFor="formName">Form Name</label>
                                <ClayInput
                                    id="formName"
                                    type="text"
                                    value={selectedTemplate.formName || ""}
                                    readOnly
                                />
                            </ClayForm.Group>
                        </ClayLayout.Col>
                        <ClayLayout.Col size={6}>
                            <ClayForm.Group>
                                <label htmlFor="type">Energy</label>
                                <ClayInput
                                    id="type"
                                    type="text"
                                    value={selectedTemplate.type?.description || ""}
                                    readOnly
                                />
                            </ClayForm.Group>
                        </ClayLayout.Col>
                        <ClayLayout.Col size={6}>
                            <ClayForm.Group>
                                <label htmlFor="category">Process</label>
                                <ClayInput
                                    id="category"
                                    type="text"
                                    value={selectedTemplate.category?.description || ""}
                                    readOnly
                                />
                            </ClayForm.Group>
                        </ClayLayout.Col>
                        <ClayLayout.Col size={12}>
                            <ClayForm.Group>
                                <label htmlFor="unit">Unit</label>
                                <ClayInput
                                    id="unit"
                                    type="text"
                                    value={selectedTemplate.unit || ""}
                                    readOnly
                                />
                            </ClayForm.Group>
                        </ClayLayout.Col>

                        <ClayLayout.Col size={12}>
                            <ClayForm.Group>
                                <label htmlFor="year">Year <span style={{ color: 'red' }}>*</span></label>
                                <DatePicker
                                    selected={year}
                                    onChange={handleYearChange}
                                    showYearPicker
                                    dateFormat="yyyy"
                                    placeholderText="Select Year"
                                    className={`form-control ${error ? 'is-invalid' : ''}`} // Add invalid class for styling if there's an error
                                />
                            </ClayForm.Group>
                        </ClayLayout.Col>

                        <ClayLayout.Col size={12}>
                            <label>Data Fields</label>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Field Name</th>
                                        <th>Value</th>
                                        <th>Unit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dataFields.map((field, index) => (
                                        <tr key={index}>
                                            <td>{field.fieldName}</td>
                                            <td>
                                                {field.selectValues.length > 0 ? (
                                                    <ClaySelect
                                                        className={!field.value || !field.value.code ? "is-invalid" : ""}
                                                        value={field.value?.code || ""}
                                                        onChange={(e) => handleFieldChange(index, e.target.value)}
                                                    >
                                                        <option value="">Select Value</option>
                                                        {field.selectValues.map((option, optIndex) => (
                                                            <option key={optIndex} value={option.code}>{option.description}</option>
                                                        ))}
                                                    </ClaySelect>
                                                ) : (
                                                    <ClayInput
                                                        type="text"
                                                        placeholder="Enter Value"
                                                        value={getFieldDisplayValue(field.value)}
                                                        onChange={(e) => handleFieldChange(index, e.target.value)}
                                                    />
                                                )}
                                            </td>
                                            <td>{field.fieldUnit || ""}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </ClayLayout.Col>
                    </ClayLayout.Row>
                </ClayForm>
            </ClayModal.Body>
            <ClayModal.Footer
                last={
                    <ClayButton.Group spaced>
                        <ClayButton displayType="secondary" onClick={onClose}>
                            Cancel
                        </ClayButton>
                        <ClayButton displayType="primary" onClick={handleSave} disabled={!year}>
                            Save
                        </ClayButton>
                    </ClayButton.Group>
                }
            />
        </ClayModal>
    );
}

export default EnergyDataModal;
